import React from 'react';
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RefundListItem } from './Refund.helper';
import { ReportFooterTimestamp } from '../../../utils/reportUtils';
import { formatDateTimeAmPm } from '../../../utils/dateUtils';
import useCurrency from '../../../components/CurrencyProvider';

export interface RefundReceiptComponentProps {
  data: RefundListItem;
}

const RefundReceiptComponent: React.FC<RefundReceiptComponentProps> = ({ data }) => {
  const { t: tBilling } = useTranslation('billing_config');
  const { formatCurrency } = useCurrency();
  // Calculate total refunded amount from payment details
  const totalRefunded = data.payment_details?.reduce((sum, p) => sum + (p.paid_amount || 0), 0) || data.refunded_amount || 0;

  return (
    <Box data-testid="refund-receipt-component">
      {/* Title */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          my: 3,
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1, height: 2, background: 'linear-gradient(to right, transparent, #e0e0e0)' }} />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 2,
            color: '#333',
          }}
        >
          {tBilling('Refund.receipt.title')}
        </Typography>
        <Box sx={{ flex: 1, height: 2, background: 'linear-gradient(to left, transparent, #e0e0e0)' }} />
      </Box>

      <Paper elevation={0} sx={{ px: 4, py: 3, borderRadius: 3, mb: 3 }}>
        {/* Receipt Info Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, borderBottom: '1px solid #e0e0e0', pb: 2 }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {tBilling('Refund.receipt.receipt_no')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, fontSize: 15 }}>
              {data.receipt_no || '-'}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {tBilling('Refund.receipt.bill_advance_number')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, fontSize: 15 }}>
              {data.bill_advance_number || '-'}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {tBilling('Refund.receipt.refund_against')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, fontSize: 15 }}>
              {data.refund_against || '-'}
            </Typography>
          </Box>
        </Box>

        {/* Date & Received By Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {tBilling('Refund.receipt.date_time')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: 14 }}>
              {formatDateTimeAmPm(data.payment_details?.[0]?.payment_date || data.refund_date)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {tBilling('Refund.receipt.refund_reason')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: 14 }}>
              {data.refund_reason || '-'}
            </Typography>
          </Box>
        </Box>

        {/* Payment Details Section */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {tBilling('Refund.receipt.payment_details')}
        </Typography>

        <TableContainer sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { borderBottom: '2px solid #e0e0e0', py: 1.5 } }}>
                <TableCell sx={{ fontWeight: 600, width: 50, color: 'text.secondary' }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>{tBilling('Refund.receipt.payment_mode')}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>{tBilling('Refund.receipt.transaction_no')}</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'right', color: 'text.secondary' }}>{tBilling('Refund.receipt.amount')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.payment_details && data.payment_details.length > 0 ? (
                data.payment_details.map((payment, index) => (
                  <TableRow key={index} sx={{ '& td': { borderBottom: '1px solid #f0f0f0', py: 1.5 } }}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{payment.payment_mode || '-'}</TableCell>
                    <TableCell>{payment.number || '-'}</TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>{formatCurrency(payment.paid_amount || 0)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                    {tBilling('Refund.receipt.no_payment_details')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Summary Box */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
            alignItems: 'center',
            bgcolor: '#e8f5e9',
            borderRadius: 2,
            border: '1px solid #c8e6c9;',
            px: 3,
            py: 2,
            mb: 3,
          }}
        >
          
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: '#2e7d32', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {tBilling('Refund.receipt.total_refunded')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: 20 }}>
              {formatCurrency(totalRefunded)}
            </Typography>
          </Box>
        </Box>

        {/* Thank you message */}
        <Typography
          variant="body2"
          sx={{
            textAlign: 'center',
            color: 'text.secondary',
            fontStyle: 'italic',
            mb: 2,
          }}
        >
          {tBilling('Refund.receipt.thank_you')}
        </Typography>

        <ReportFooterTimestamp />
      </Paper>
    </Box>
  );
};

export default RefundReceiptComponent;
